import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Crown } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  acceptDirectorInvite,
  previewDirectorInvite,
} from '../lib/api/admin';
import { showErrorToast, showSuccessToast } from '../lib/toast';
import { ApiError } from '../lib/api/core';
import { isPasswordValid, PASSWORD_MIN_LENGTH } from '@/lib/passwordPolicy';

// Story 063 — landing page for /director-invite/:token. Anonymous access
// (no auth required). Validates the token, shows the email it was sent
// to, lets the invitee set a password and complete signup. On success
// the user is created with app_metadata.is_director = true.

type Phase =
  | { kind: 'loading' }
  | { kind: 'ready'; email: string }
  | { kind: 'error'; message: string }
  | { kind: 'done'; email: string };

export function DirectorInviteAcceptPage() {
  const { t } = useTranslation('director');
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setPhase({ kind: 'error', message: t('director_invite_accept.missing_token') });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { email } = await previewDirectorInvite(token);
        if (!cancelled) setPhase({ kind: 'ready', email });
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof ApiError
            ? err.userMessage
            : t('director_invite_accept.validate_failed');
        setPhase({ kind: 'error', message });
      }
    })();
    return () => {
      cancelled = true;
    };
    // t intentionally omitted — stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!token || !isPasswordValid(password)) {
      showErrorToast(t('director_invite_accept.weak_password_toast'));
      return;
    }
    setSubmitting(true);
    try {
      const { email } = await acceptDirectorInvite(token, password);
      setPhase({ kind: 'done', email });
      showSuccessToast(t('director_invite_accept.account_created_toast'));
      // Redirect to login so the new director can sign in.
      setTimeout(() => navigate('/login'), 1500);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.userMessage
          : t('director_invite_accept.accept_failed_toast');
      showErrorToast(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-md space-y-4">
        <div className="flex items-center gap-2">
          <Crown className="h-6 w-6 text-accent-foreground" />
          <h1 className="text-xl font-semibold">{t('director_invite_accept.brand_heading')}</h1>
        </div>

        {phase.kind === 'loading' && (
          <p className="text-muted-foreground">{t('director_invite_accept.loading')}</p>
        )}

        {phase.kind === 'error' && (
          <div
            data-testid="invite-error"
            className="rounded border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive"
          >
            {phase.message}
          </div>
        )}

        {phase.kind === 'ready' && (
          <form
            onSubmit={handleSubmit}
            className="space-y-4 rounded border bg-card p-6"
          >
            <h2 className="text-lg font-semibold">{t('director_invite_accept.form_title')}</h2>
            <p className="text-sm text-muted-foreground">
              <Trans
                i18nKey="director_invite_accept.inviting_intro"
                t={t}
                values={{ email: phase.email }}
                components={{ email: <span className="font-mono" /> }}
              />
            </p>
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="signup-password">
                {t('director_invite_accept.password_label')}
              </label>
              <Input
                id="signup-password"
                type="password"
                required
                minLength={PASSWORD_MIN_LENGTH}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="signup-password-input"
                autoComplete="new-password"
              />
            </div>
            <Button
              type="submit"
              className="w-full"
              data-testid="signup-submit"
              disabled={submitting}
            >
              {submitting
                ? t('director_invite_accept.submitting')
                : t('director_invite_accept.submit_button')}
            </Button>
          </form>
        )}

        {phase.kind === 'done' && (
          <div
            data-testid="invite-done"
            className="rounded border bg-card p-6 text-sm"
          >
            {t('director_invite_accept.done_message')}
          </div>
        )}
      </div>
    </div>
  );
}
